<template>
  <div class="calendar" :class="{ mobile: isMobile }" dir="rtl">
    <div class="calendar-header">
      <div class="nav-month">
        <button type="button" class="nav-btn" aria-label="חודש קודם" @click="prevMonth" :disabled="!canGoPrev">‹</button>
        <span class="month-year">{{ monthLabel }} {{ displayYear }}</span>
        <button type="button" class="nav-btn" aria-label="חודש הבא" @click="nextMonth" :disabled="!canGoNext">›</button>
      </div>
      <div class="legend">
        <div class="legend-item">
          <svg width="8" height="8" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="4" fill="rgba(23, 67, 122, 0.44)" />
          </svg>
          <span>יום לימודים רגיל</span>
        </div>
        <div class="legend-item">
          <svg width="8" height="8" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="4" fill="#FFC56F" />
          </svg>
          <span>יום חג/שבתון</span>
        </div>
      </div>
    </div>
    <div class="weekdays">
      <span v-for="d in weekDayNames" :key="d" class="weekday">{{ d }}</span>
    </div>
    <div class="days-grid">
      <div v-for="cell in calendarCells" :key="cell.key" class="day-cell" :class="{
        other: cell.other,
        today: cell.isToday,
        holiday: cell.isHoliday,
        'evening-holiday': cell.isEveningHoliday,
        empty: cell.isEmpty,
        past: cell.isPast,
        locked: cell.isLocked,
        'after-max': cell.isAfterMax
      }">
        <template v-if="cell.day">
          <div class="day-header-row">
            <span class="day-num">{{ cell.day }}</span>
            <span v-if="cell.isHoliday" class="holiday-name">{{ cell.holidayName }}</span>
          </div>
          <div class="cell-body-center">
            <slot name="cell-body" :cell="cell">
              <div class="hours-control">
                <button type="button" class="hour-btn increase" @click.stop="adjustHours(cell, 1)"
                  aria-label="הוסף שעות" :disabled="cell.isPast || cell.isLocked || cell.isAfterMax">+</button>
                <div class="hours-display">
                  <span class="hours-num">{{ cell.hours }}</span>
                </div>
                <button type="button" class="hour-btn decrease" @click.stop="adjustHours(cell, -1)"
                  aria-label="הפחת שעות" :disabled="cell.isPast || cell.isLocked || cell.isAfterMax">−</button>
              </div>
              <span class="hours-label">שעות לימוד</span>
            </slot>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'Calendar',
  props: {
    selectedDate: { type: [String, Date], default: null },
    events: { type: Array, default: function () { return []; } },
    constraintDays: { type: Array, default: function () { return []; } },
    holidays: { type: Array, default: function () { return []; } },
    weeklyHours: { type: Array, default: function () { return [8, 8, 8, 8, 8, 4, 0]; } },
    isMobile: { type: Boolean, default: false },
    showHint: { type: Boolean, default: true },
    minDate: { type: String, default: null },
    maxDate: { type: String, default: null }
  },
  data() {
    return {
      weekDayNames: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
      displayMonth: new Date().getMonth(),
      displayYear: new Date().getFullYear()
    };
  },
  watch: {
    minDate: {
      immediate: true,
      handler(val) {
        if (val) {
          var d = new Date(val);
          this.displayMonth = d.getMonth();
          this.displayYear = d.getFullYear();
        }
      }
    }
  },
  computed: {
    canGoPrev() {
      if (!this.minDate) return true;
      var min = new Date(this.minDate);
      return this.displayYear > min.getFullYear() ||
        (this.displayYear === min.getFullYear() && this.displayMonth > min.getMonth());
    },
    canGoNext() {
      if (!this.maxDate) return true;
      var max = new Date(this.maxDate);
      return this.displayYear < max.getFullYear() ||
        (this.displayYear === max.getFullYear() && this.displayMonth < max.getMonth());
    },
    monthLabel() {
      const months = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
      return months[this.displayMonth];
    },
    calendarCells() {
      const year = this.displayYear;
      const month = this.displayMonth;
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const firstDay = first.getDay();
      const totalDays = last.getDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const minDateObj = this.minDate ? new Date(this.minDate + 'T00:00:00') : null;
      const maxDateObj = this.maxDate ? new Date(this.maxDate + 'T00:00:00') : null;
      const pastCutoff = (minDateObj && minDateObj.getTime() > today.getTime()) ? minDateObj : today;

      const constraintMap = {};
      (this.constraintDays || []).forEach((item) => {
        const d = typeof item.date === 'string' ? item.date : (item.date && item.date.toISOString ? item.date.toISOString().slice(0, 10) : '');
        if (d) constraintMap[d] = { hours: item.hours, isLocked: item.isLocked };
      });

      // Build holiday map: { "2026-04-01": { name: "פסח", isEvening: false }, ... }
      const holidayMap = {};
      (this.holidays || []).forEach((holiday) => {
          // Object format with name
          holidayMap[holiday.date] = { 
            name: holiday.name || '', 
            isEvening: holiday.isEvening || false 
          };
      });
      var cells = [];
      var pad = firstDay;
      // Add empty cells at the beginning
      for (var i = 0; i < pad; i++) {
        cells.push({ key: 'pad-start-' + i, other: true, day: null, isEmpty: true });
      }
      // Add actual month days
      for (var d = 1; d <= totalDays; d++) {
        const dateStr = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
        const cellDate = new Date(year, month, d);
        cellDate.setHours(0, 0, 0, 0);
        const isToday = cellDate.getTime() === today.getTime();
        const holidayInfo = holidayMap[dateStr];
        const isHoliday = holidayInfo !== undefined;
        const dayOfWeek = cellDate.getDay();
        
        // Determine default hours based on holiday type
        let defaultHours;
        if (isHoliday && holidayInfo.isEvening) {
          // Evening holiday (ערב חג) - shortened day
          defaultHours = 4;
        } else if (isHoliday) {
          // Full holiday - no learning
          defaultHours = 0;
        } else {
          // Regular day - use weekly schedule
          defaultHours = this.weeklyHours[dayOfWeek] != null ? this.weeklyHours[dayOfWeek] : 8;
        }
        
        // Apply user constraints (overrides default)
        const constraint = constraintMap[dateStr];
        const isBeforeMin = minDateObj ? cellDate.getTime() < minDateObj.getTime() : false;
        const isAfterMax = maxDateObj ? cellDate.getTime() > maxDateObj.getTime() : false;
        const hours = isBeforeMin || isAfterMax ? 0 : (constraint !== undefined ? constraint.hours : defaultHours);
        const isLocked = constraint ? constraint.isLocked : false;

        cells.push({
          key: dateStr,
          day: d,
          dateStr: dateStr,
          date: cellDate,
          isToday: isToday,
          isPast: cellDate.getTime() < pastCutoff.getTime(),
          isLocked: isLocked,
          isAfterMax: isAfterMax,
          isHoliday: isHoliday,
          holidayName: holidayInfo ? holidayInfo.name : '',
          isEveningHoliday: holidayInfo ? holidayInfo.isEvening : false,
          hours: hours
        });
      }
      // Add empty cells at the end to complete the grid (multiples of 7)
      var totalCells = cells.length;
      var remainingCells = totalCells % 7; // seven days in a week
      if (remainingCells > 0) {
        var cellsToAdd = 7 - remainingCells;
        for (var j = 0; j < cellsToAdd; j++) {
          cells.push({ key: 'pad-end-' + j, other: true, day: null, isEmpty: true });
        }
      }
      console.log("cells", cells)
      return cells;
    }
  },
  methods: {
    prevMonth() {
      if (!this.canGoPrev) return;
      if (this.displayMonth === 0) {
        this.displayMonth = 11;
        this.displayYear--;
      } else {
        this.displayMonth--;
      }
      this.$emit('month-change', { month: this.displayMonth, year: this.displayYear });
    },
    nextMonth() {
      if (!this.canGoNext) return;
      if (this.displayMonth === 11) {
        this.displayMonth = 0;
        this.displayYear++;
      } else {
        this.displayMonth++;
      }
      this.$emit('month-change', { month: this.displayMonth, year: this.displayYear });
    },
    adjustHours(cell, delta) {
      // Allow hour adjustment even on holidays
      var newHours = cell.hours + delta;
      if (newHours < 0) newHours = 0;
      if (newHours > 24) newHours = 24;
      this.$emit('hours-change', { date: cell.dateStr, hours: newHours, day: cell.day });
    }
  }
};
</script>

<style scoped>
.calendar {
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  font-family: Arimo, Arial, sans-serif;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.legend {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #03182a;
}

.legend-item svg {
  flex-shrink: 0;
}

.nav-btn {
  min-width: 16px;
  min-height: 16px;
  border: 1px solid #00427E;
  background: #00427E;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
}

.nav-btn:hover {
  background: #00427E;
  color: #fff;
}

.month-year {
  min-width: 120px;
  font-weight: bold;
  font-size: 18px;
  color: #03182a;
  text-align: center;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 0.5rem;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  background-color: #fff;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.25);
  padding: 0.5rem;

  .weekday {
    text-align: center;
    font-size: 0.8rem;
  }
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.day-cell {
  min-height: 90px;
  width: 100%;
  padding: 8px 4px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: center;
  justify-content: flex-start;
  font-size: 14px;
  gap: 4px;
  min-width: 0;
  box-sizing: border-box;
}

.day-cell.empty {
  background: #FFF;
}

.day-cell.today {
  border: 2px solid #17437A;
  box-shadow: 0 2px 8px rgba(23, 67, 122, 0.3);
}

.day-cell.past {
  opacity: 0.4;
}

.day-cell.past .hour-btn {
  cursor: not-allowed;
  pointer-events: none;
}

.day-cell.locked {
  opacity: 0.4;
}

.day-cell.locked .hour-btn {
  cursor: not-allowed;
  pointer-events: none;
}

.day-cell.after-max {
  opacity: 0.4;
  background: #e8e8e8;
}

.day-cell.after-max .hour-btn {
  cursor: not-allowed;
  pointer-events: none;
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.day-cell.holiday {
  background: #FFC56F7D;
  color: #17437A;
}

.day-cell.holiday .hours-control {
  background: #FFC56E7D;
}

.day-cell.holiday .hours-num {
  color: #E1583D;
}

.day-cell.holiday .hour-btn {
  border-color: #E1583D;
  color: #E1583D;
}

.day-cell.holiday:not(.evening-holiday) .hour-btn {
background:transparent;
}

.day-cell.holiday .hour-btn:hover {
  background: #E1583D;
  color: #fff;
}

.day-cell.holiday.evening-holiday {
  background: linear-gradient(to bottom, #fff 50%, #FFC56F7D 50%);
}

.day-cell.holiday.evening-holiday .hours-control {
  background: #F0F2F5;
}

.day-cell.holiday .day-num {
  color: #E1583D;
}

.day-cell.holiday.evening-holiday .hours-num {
  color: black;
}

.day-cell.holiday.evening-holiday .hour-btn {
  border-color: #17437A;
  color: #17437A;
}

.day-cell.holiday.evening-holiday .hour-btn:hover {
  background: #17437A;
  color: #fff;
}

.day-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: baseline;
  width: 100%;
  padding: 0 4px;
}

.day-num {
  font-weight: bold;
  font-size: 14px;
  color: rgba(23, 67, 122, 0.44);
  flex-shrink: 0;
}

.holiday-name {
  color: #E1583D;
  font-size: 9px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.cell-body-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.hours-control {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0.3rem;
  justify-content: center;
  border-radius: 10px;
  background: #F0F2F5;
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.25) inset;
}

.hour-btn {
  background-color: #FFF;
  border: 1px solid #17437A;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  line-height: 0;
  color: #17437A;
}

.hour-btn:hover {
  background: #17437A;
  color: #fff;
}

.hours-display {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
}

.hours-num {
  font-size: 16px;
  font-weight: bold;
  color: black;
}

.hours-label {
  font-size: 9px;
  color: #666;
  text-align: center;
}

.calendar.mobile .day-cell {
  min-height: 90px;
  font-size: 11px;
}

.circle-container {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  transform: translate(-80%, 80%);
}

.nav-month {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .calendar {
    padding: 0.75rem;
  }

  .calendar-header {
    margin-bottom: 0.75rem;
  }

  .month-year {
    font-size: 16px;
    min-width: 130px;
  }

  .legend {
    gap: 0.5rem;
  }

  .legend-item {
    font-size: 12px;
    gap: 4px;
  }

  .circle-container {
    display: none;
  }

  .nav-btn {
    min-width: 38px;
    min-height: 38px;
    font-size: 18px;
  }

  .weekdays {
    font-size: 11px;
  }

  .day-cell {
    min-height: 95px;
    font-size: 12px;
    padding: 6px 4px;
  }

  .day-num {
    font-size: 13px;
  }

  .hours-num {
    font-size: 14px;
  }

  .hours-label {
    font-size: 8px;
  }
}

@media (max-width: 480px) {
  .calendar {
    padding: 0.5rem;
  }

  .month-year {
    font-size: 14px;
  }

  .legend {
    gap: 0.25rem;
  }

  .legend-item {
    font-size: 10px;
    gap: 3px;
  }

  .legend-item svg {
    width: 6px;
    height: 6px;
  }

  .nav-btn {
    min-width: 36px;
    min-height: 36px;
    font-size: 16px;
  }

  .weekdays {
    font-size: 10px;
    gap: 2px;
  }

  .days-grid {
    gap: 2px;
  }

  .day-cell {
    min-height: 85px;
    padding: 4px 2px;
    font-size: 10px;
    gap: 2px;
  }

  .day-num {
    font-size: 12px;
  }

  .hours-num {
    font-size: 13px;
  }

  .hours-label {
    font-size: 7px;
  }

  .hour-btn {
    width: 16px;
    height: 16px;
    font-size: 12px;
  }

  .holiday-badge {
    font-size: 8px;
    padding: 1px 4px;
  }

  .hours-control {
    display: flex;
    align-items: center;
    padding: 0.1rem;
    justify-content: center;
    border-radius: 10px;
    background: #F0F2F5;
    box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.25) inset;
  }

  .hour-btn {
    background-color: #FFF;
    border: 1px solid #17437A;
    border-radius: 50%;
    width: 8px;
    height: 8px;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    line-height: 1;
    color: #17437A;
  }

  .hours-display {
    min-width: 8px;
  }

  .hours-num {
    font-size: 10px;
  }
}
</style>
